import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { generateURLParams } from './service-utilities';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

const NS_SILICONE_CLASS_ID = 17;

@Injectable({
  providedIn: 'root',
})
export class NetsuiteService {
  private accountId = '';
  private configRequest: Observable<any> | null = null;
  private estimateVersionsRequests: { [dealId: string]: Observable<any> } = {};

  constructor(
    private http: HttpClient,
  ) { }

  getConfig() {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/config`);
  }

  // Public (non admin-gated) app config - used by loadConfig() below to read just the
  // NetSuite accountId without hitting the admin-only /netsuite/config route.
  getPublicConfig() {
    return this.http.get<any>(`${environment.apiUrl}/config`);
  }

  // Caches the NetSuite accountId (used for building record deep-links) via the
  // PUBLIC `/config` endpoint - NOT `getConfig()` above, which hits the admin-only
  // `/netsuite/config` route and 403s for regular users. This is called from the
  // app's APP_INITIALIZER for every user (including logged-out ones), so it must
  // never reject - hence the catchError fallback.
  loadConfig(): Observable<any> {
    if (!this.configRequest) {
      this.configRequest = this.getPublicConfig().pipe(
        tap((data) => {
          if (data?.success) {
            this.accountId = data.result?.netsuite?.accountId || '';
          }
        }),
        catchError(() => of(null)),
        shareReplay(1),
      );
    }

    return this.configRequest;
  }

  updateConfig(data) {
    return this.http.post<any>(`${environment.apiUrl}/netsuite/config`, data);
  }

  testConnection() {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/test_connection`);
  }

  getClasses(params) {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/get_classes${generateURLParams(params)}`);
  }

  getLocations(params) {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/get_locations${generateURLParams(params)}`);
  }

  getTaxCodes(params) {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/get_tax_codes${generateURLParams(params)}`);
  }

  getItems(params) {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/get_items${generateURLParams(params)}`);
  }

  getSiliconeTypeItems() {
    return this.getItems({ type: 'non-inventory', classId: NS_SILICONE_CLASS_ID });
  }

  getFilmItems(classId, manufacturer) {
    return this.getItems({ type: 'non-inventory', classId, manufacturer });
  }

  getDiscounts(params) {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/get_discounts${generateURLParams(params)}`);
  }

  getCustomerCategories(params) {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/get_customer_categories${generateURLParams(params)}`);
  }

  getEmployees(params) {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/get_employees${generateURLParams(params)}`);
  }

  createInvoice(data) {
    return this.http.post<any>(`${environment.apiUrl}/netsuite/create_invoice`, data);
  }

  updateInvoice(data) {
    return this.http.post<any>(`${environment.apiUrl}/netsuite/update_invoice`, data);
  }

  createSalesOrder(params, data) {
    return this.http.post<any>(`${environment.apiUrl}/netsuite/create_sales_order${generateURLParams(params)}`, data);
  }

  updateSalesOrder(params, data) {
    return this.http.post<any>(`${environment.apiUrl}/netsuite/update_sales_order${generateURLParams(params)}`, data);
  }

  sendTransactionEmail(data) {
    return this.http.post<any>(`${environment.apiUrl}/netsuite/send_transaction_email`, data);
  }

  getInvoices(params) {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/invoices${generateURLParams(params)}`);
  }

  getCreditMemos(params) {
    return this.http.get<any>(`${environment.apiUrl}/netsuite/credit-memos${generateURLParams(params)}`);
  }

  getInvoiceTransactions(params): Observable<any[]> {
    return forkJoin({
      invoices: this.getInvoices(params),
      creditMemos: this.getCreditMemos(params),
    }).pipe(map(({ invoices, creditMemos }) => {
      if (!invoices.success || !creditMemos.success) {
        return [];
      }

      return [
        ...invoices.result.map(invoice => ({
          ...invoice,
          invoiceId: invoice.id,
          invoiceNumber: invoice.number,
          recordType: 'Invoice',
        })),
        ...creditMemos.result.map(creditMemo => ({
          ...creditMemo,
          amountRemaining: -creditMemo.amountRemaining,
          recordType: 'Credit Memo',
        })),
      ].sort(this.compareInvoiceTransactions);
    }));
  }

  getEstimateVersions(dealId: string) {
    if (!this.estimateVersionsRequests[dealId]) {
      this.estimateVersionsRequests[dealId] = this.http.get<any>(
        `${environment.apiUrl}/netsuite/estimate_versions/${encodeURIComponent(dealId)}`,
      ).pipe(shareReplay(1));
    }

    return this.estimateVersionsRequests[dealId];
  }

  clearEstimateVersionsCache(dealId: string): void {
    delete this.estimateVersionsRequests[dealId];
  }

  createEstimateVersions(data) {
    return this.http.post<any>(`${environment.apiUrl}/netsuite/estimate_versions`, data).pipe(
      tap(() => {
        this.clearEstimateVersionsCache(data.deal);
      }),
    );
  }

  salesOrderUrl(nsId: string): string {
    return this.recordUrl('/app/accounting/transactions/transaction.nl', nsId);
  }

  estimateUrl(nsId: string): string {
    return this.recordUrl('/app/accounting/transactions/estimate.nl', nsId);
  }

  invoiceUrl(nsId: string): string {
    return this.recordUrl('/app/accounting/transactions/custinvc.nl', nsId);
  }

  creditMemoUrl(nsId: string): string {
    return this.recordUrl('/app/accounting/transactions/custcred.nl', nsId);
  }

  customerUrl(nsId: string): string {
    return this.recordUrl('/app/common/entity/custjob.nl', nsId);
  }

  contactUrl(nsId: string): string {
    return this.recordUrl('/app/common/entity/contact.nl', nsId);
  }

  refreshCache() {
    return this.http.post<any>(`${environment.apiUrl}/netsuite/refresh_cache`, {});
  }

  private recordUrl(path: string, nsId: string): string {
    this.loadConfig();
    if (!this.accountId || !nsId) {
      return '';
    }

    return `https://${this.accountId}.app.netsuite.com${path}?id=${nsId}`;
  }

  private compareInvoiceTransactions(a: any, b: any): number {
    const dateOrder = new Date(b.date).getTime() - new Date(a.date).getTime();
    if (dateOrder !== 0) {
      return dateOrder;
    }

    const invoiceOrder = b.invoiceNumber - a.invoiceNumber;
    if (invoiceOrder !== 0) {
      return invoiceOrder;
    }

    if (a.recordType !== b.recordType) {
      return a.recordType === 'Credit Memo' ? -1 : 1;
    }

    return b.number - a.number;
  }
}
